import React, { useState, useRef, useEffect } from 'react';
import { FiArrowLeft, FiPlus, FiMinus, FiFileText } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import api from '../api/axios';
import PDFViewer from './PDFViewer';
import PDFIframePreview from './PDFIframePreview';
import heic2any from 'heic2any';
import './PrintOrderConfig.css';


const ImagePreview = ({ file, color, scale, orientation }) => {
  const isLandscape = orientation === 'landscape';
  const [imageSrc, setImageSrc] = useState(null);

  useEffect(() => {
    if (!file) return;
    
    if (isHeic(file.name)) {
      // Convert HEIC to JPEG blob URL
      const blob = file.viewLink ? null : file.file;
      if (blob && blob instanceof Blob) {
        convertHeicToBlobUrl(blob).then(url => {
          if (url) setImageSrc(url);
        });
      } else if (file.thumbnailLink) {
        // Use thumbnail if available
        setImageSrc(file.thumbnailLink);
      }
    } else {
      // Regular image
      setImageSrc(file.thumbnailLink || file.viewLink);
    }
  }, [file]);

  const sheetStyle = {
    background: '#ffffff',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
    borderRadius: '2px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    overflow: 'hidden',
    boxSizing: 'border-box',
    width: '100%',
    height: '100%',
    aspectRatio: isLandscape ? '1.414 / 1' : '1 / 1.414',
    maxWidth: isLandscape ? '100%' : 'calc(100% * 0.707 / 0.75)',
    maxHeight: isLandscape ? 'calc(100% * 0.75 / 1.414)' : '100%',
    transition: 'all 0.3s ease'
  };

  if (!imageSrc) {
    return (
      <div className={`a4-sheet-container ${isLandscape ? 'landscape' : 'portrait'}`} style={sheetStyle}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem' }}>
          Loading preview...
        </div>
      </div>
    );
  }

  return (
    <div className={`a4-sheet-container ${isLandscape ? 'landscape' : 'portrait'}`} style={sheetStyle}>
      <img
        src={imageSrc}
        alt={file.name}
        className="poc-slide-img"
        style={{
          filter: color === 'bw' ? 'grayscale(100%)' : undefined,
          transform: `scale(${(scale || 90) / 100})`,
          transition: 'filter 0.2s, transform 0.2s',
          maxWidth: '100%',
          maxHeight: '100%',
          objectFit: 'contain'
        }}
      />
    </div>
  );
};

const parsePageRange = (rangeStr, maxPages) => {
  if (!rangeStr || !rangeStr.trim()) return [];
  const pages = new Set();
  const parts = rangeStr.split(',');
  for (const part of parts) {
    const trimmed = part.trim();
    if (/^\d+$/.test(trimmed)) {
      const page = parseInt(trimmed, 10);
      if (page >= 1 && page <= maxPages) {
        pages.add(page);
      }
    } else if (/^\d+-\d+$/.test(trimmed)) {
      const [startStr, endStr] = trimmed.split('-');
      const start = parseInt(startStr, 10);
      const end = parseInt(endStr, 10);
      if (start >= 1 && end >= 1 && start <= maxPages && end <= maxPages) {
        const first = Math.min(start, end);
        const last = Math.max(start, end);
        for (let p = first; p <= last; p++) {
          pages.add(p);
        }
      }
    }
  }
  return Array.from(pages).sort((a, b) => a - b);
};

const formatPageRange = (pages) => {
  if (!pages || pages.length === 0) return 'none';
  const sorted = [...pages].sort((a, b) => a - b);
  const ranges = [];
  let start = sorted[0];
  let end = sorted[0];
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i] === end + 1) {
      end = sorted[i];
    } else {
      ranges.push(start === end ? `${start}` : `${start}-${end}`);
      start = sorted[i];
      end = sorted[i];
    }
  }
  ranges.push(start === end ? `${start}` : `${start}-${end}`);
  return ranges.join(', ');
};

const getSelectionSummary = (c, fileCount) => {
  if (c.pageSelection === 'all') return `${c.pages} pages`;
  if (c.pageSelection === 'range') return `${c.pages} pages (${c.pageRange || 'none'})`;
  return `${c.pages} pages (${formatPageRange(c.customPages)})`;
};

const defaultConfig = () => ({
  copies: 1,
  pageSelection: 'all', // 'all' | 'range' | 'custom'
  pageRange: '',
  customPages: [],
  pages: 1,
  color: 'bw',
  orientation: 'portrait',
  sides: 'single', // 'single' | 'both'
  size: 'A4',
  quality: 'standard', // 'standard' | 'high'
  scale: 90
});

const getInitialConfig = (file) => {
  const total = file.pageCount || 1;
  return {
    copies: 1,
    pageSelection: 'all',
    pageRange: '',
    customPages: Array.from({ length: total }, (_, i) => i + 1),
    pages: total,
    color: 'bw',
    orientation: 'portrait',
    sides: 'single',
    size: 'A4',
    quality: 'standard',
    scale: 90
  };
};

const getFileIcon = (name = '') => {
  const ext = name.split('.').pop().toLowerCase();
  const map = { pdf: '📄', jpg: '🖼️', jpeg: '🖼️', png: '🖼️', webp: '🖼️', heic: '🖼️', heif: '🖼️', doc: '📝', docx: '📝', xls: '📊', xlsx: '📊', ppt: '📽️', pptx: '📽️' };
  return map[ext] || '📁';
};

const isImage = (name = '') => ['jpg', 'jpeg', 'png', 'webp', 'heic', 'heif'].includes(name.split('.').pop().toLowerCase());

const isHeic = (name = '') => ['heic', 'heif'].includes(name.split('.').pop().toLowerCase());

// Convert HEIC/HEIF to JPEG blob URL for preview
const convertHeicToBlobUrl = async (file) => {
  try {
    const blob = await heic2any({
      blob: file,
      toType: 'image/jpeg',
      quality: 0.9
    });
    return URL.createObjectURL(blob);
  } catch (err) {
    console.error('HEIC conversion failed:', err);
    return null;
  }
};

const isPdfFile = (name = '', mimeType = '') => 
  mimeType === 'application/pdf' || name?.toLowerCase().endsWith('.pdf');

const isDocxFile = (name = '', mimeType = '') => 
  mimeType?.includes('word') || 
  mimeType?.includes('powerpoint') || 
  mimeType?.includes('presentation') || 
  mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || 
  mimeType === 'application/msword' || 
  mimeType === 'application/vnd.openxmlformats-officedocument.presentationml.presentation' || 
  mimeType === 'application/vnd.ms-powerpoint' || 
  name?.toLowerCase().endsWith('.docx') || 
  name?.toLowerCase().endsWith('.doc') || 
  name?.toLowerCase().endsWith('.pptx') || 
  name?.toLowerCase().endsWith('.ppt');

const PrintOrderConfig = ({ uploadedFiles, onAddMoreFiles, onBack, initialConfigs, editCartItem }) => {
  const navigate = useNavigate();
  const { addToCart, updateCartItem } = useAuth();
  const isEditMode = !!editCartItem;
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [current, setCurrent] = useState(0);
  const [configs, setConfigs] = useState(() =>
    initialConfigs && initialConfigs.length === uploadedFiles.length
      ? initialConfigs
      : uploadedFiles.map(f => getInitialConfig(f))
  );
  const sliderRef = useRef(null);
  const addFileRef = useRef(null);
  const [colorPrice, setColorPrice] = useState({ bw: 3, colour: 10 });

  useEffect(() => {
    const fetchRates = async () => {
      try {
        const { data } = await api.get('/print-rates');
        if (data.success && data.rates) {
          const bwRate = data.rates.find(r => r.key === 'file_bw')?.value ?? 3;
          const colorRate = data.rates.find(r => r.key === 'file_color')?.value ?? 10;
          setColorPrice({ bw: bwRate, colour: colorRate });
        }
      } catch (err) {
        console.warn('Failed to fetch print rates in PrintOrderConfig:', err);
      }
    };
    fetchRates();
  }, []);

  // Sync configs if more files are added
  useEffect(() => {
    setConfigs(prev => {
      if (uploadedFiles.length > prev.length) {
        const extra = uploadedFiles.slice(prev.length).map(f => getInitialConfig(f));
        return [...prev, ...extra];
      }
      return prev.slice(0, uploadedFiles.length);
    });
  }, [uploadedFiles.length]);

  // Scroll slider to current slide
  useEffect(() => {
    if (sliderRef.current) {
      const child = sliderRef.current.children[current];
      if (child) child.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
  }, [current]);

  const file = uploadedFiles[current];

  const updateConfig = (key, value) => {
    setConfigs(prev => prev.map((c, i) => {
      if (i !== current) return c;
      const updated = { ...c, [key]: value };
      const currentFile = uploadedFiles[i];

      // Recalculate physical pages to be printed
      let calculatedPages = currentFile?.pageCount || 1;
      if (updated.pageSelection === 'all') {
        calculatedPages = currentFile?.pageCount || 1;
      } else if (updated.pageSelection === 'range') {
        const parsed = parsePageRange(updated.pageRange, getEffectiveMaxPages(currentFile));
        calculatedPages = parsed.length;
      } else if (updated.pageSelection === 'custom') {
        calculatedPages = updated.customPages.length;
      }

      updated.pages = calculatedPages;
      return updated;
    }));
  };

  const handlePageToggle = (pageNum) => {
    const currentConfig = configs[current] || defaultConfig();
    let updatedCustomPages = [...(currentConfig.customPages || [])];

    if (updatedCustomPages.includes(pageNum)) {
      updatedCustomPages = updatedCustomPages.filter(p => p !== pageNum);
    } else {
      updatedCustomPages.push(pageNum);
      updatedCustomPages.sort((a, b) => a - b);
    }

    updateConfig('customPages', updatedCustomPages);
  };

  const applyToAll = () => {
    const base = configs[current];
    setConfigs(prev => prev.map((c, i) => {
      const currentFile = uploadedFiles[i];
      const copy = { ...base };

      // Preserve file-specific page settings limits
      if (copy.pageSelection === 'all') {
        copy.pages = currentFile?.pageCount || 1;
        copy.customPages = Array.from({ length: currentFile?.pageCount || 1 }, (_, index) => index + 1);
      } else if (copy.pageSelection === 'range') {
        const parsed = parsePageRange(copy.pageRange, getEffectiveMaxPages(currentFile));
        copy.pages = parsed.length;
      } else if (copy.pageSelection === 'custom') {
        // If copying custom pages selection, keep valid pages only
        copy.customPages = base.customPages.filter(p => p <= getEffectiveMaxPages(currentFile));
        copy.pages = copy.customPages.length;
      }

      return copy;
    }));
    toast.success('Settings applied to all files');
  };

  // Compute total pages (sheets) and total price based on sheets used
  const totalPages = configs.reduce((sum, c, i) => {
    if (!uploadedFiles[i]) return sum;
    const isDouble = c.sides === 'both';
    const sheets = isDouble ? Math.ceil(c.pages / 2) : c.pages;
    return sum + c.copies * sheets;
  }, 0);

  const totalPrice = configs.reduce((sum, c, i) => {
    if (!uploadedFiles[i]) return sum;
    const isDouble = c.sides === 'both';
    const sheets = isDouble ? Math.ceil(c.pages / 2) : c.pages;
    return sum + c.copies * sheets * colorPrice[c.color];
  }, 0);

  const handleAddMoreFiles = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) onAddMoreFiles?.(files);
    if (addFileRef.current) addFileRef.current.value = '';
  };

  const buildCartItemFromFile = (f, cfg) => {
    const pricePerPage = colorPrice[cfg.color];
    const isDouble = cfg.sides === 'both';
    const sheets = isDouble ? Math.ceil(cfg.pages / 2) : cfg.pages;
    const fileTotalPrice = cfg.copies * sheets * pricePerPage;
    return {
      id: f.id,
      type: 'print',
      name: f.name,
      price: fileTotalPrice,
      quantity: 1,
      image: f.thumbnailLink || f.viewLink || null,
      fileUrl: f.viewLink || f.thumbnailLink || null,
      pageCount: f.pageCount,
      mimeType: f.mimeType,
      driveFileId: f.id,
      printConfig: { ...cfg }
    };
  };

  const handleAddToCart = () => {
    // Validation
    const invalidConfig = configs.some((cfg, i) => {
      if (cfg.pageSelection === 'range') {
        const parsed = parsePageRange(cfg.pageRange, getEffectiveMaxPages(uploadedFiles[i]));
        return parsed.length === 0;
      }
      if (cfg.pageSelection === 'custom') {
        return cfg.customPages.length === 0;
      }
      return cfg.pages <= 0;
    });

    if (invalidConfig) {
      toast.error('One or more files have no pages selected or an invalid range.');
      return;
    }

    if (isEditMode) {
      // Edit mode: update the existing cart item
      const f = uploadedFiles[0];
      const cfg = configs[0] || defaultConfig();
      const updatedItem = buildCartItemFromFile(f, cfg);
      updateCartItem(editCartItem.id, editCartItem.type, updatedItem);
      toast.success('Cart updated! ✅');
    } else {
      // Add mode: add all files to cart
      uploadedFiles.forEach((f, i) => {
        const cfg = configs[i] || defaultConfig();
        addToCart(buildCartItemFromFile(f, cfg));
      });
      toast.success('Added to cart! ✅');
    }

    navigate('/checkout');
  };

  const handleBack = () => {
    if (isEditMode) {
      setShowDiscardDialog(true);
    } else {
      onBack();
    }
  };

  const handleDiscardAndGoBack = () => {
    setShowDiscardDialog(false);
    navigate('/checkout');
  };

  const handleUpdateAndGoBack = () => {
    setShowDiscardDialog(false);
    handleAddToCart();
  };

  const cfg = configs[current] || defaultConfig();
  const isDocx = isDocxFile(file?.name, file?.mimeType);
  const isPdf = isPdfFile(file?.name, file?.mimeType) || isDocx;

  const getEffectiveMaxPages = (file) => {
    if (!file) return 1;
    const isWord = isDocxFile(file.name, file.mimeType);
    if (isWord && (file.pageCount || 1) === 1) {
      return 9999;
    }
    return file.pageCount || 1;
  };

  return (
    <div className="poc-page">
      {/* Header */}
      <div className="poc-header">
        <button className="poc-back" onClick={handleBack}>
          <FiArrowLeft size={20} />
        </button>
        <span className="poc-header-title">Print Setup</span>
        <button className="poc-add-files-btn" onClick={() => addFileRef.current?.click()}>
          <FiPlus size={16} />
          Add files
        </button>
        <input
          ref={addFileRef}
          type="file"
          multiple
          accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.ppt,.pptx"
          onChange={handleAddMoreFiles}
          style={{ display: 'none' }}
        />
      </div>

      {/* File Slider */}
      <div className="poc-slider-wrap">
        <div className="poc-slider" ref={sliderRef}>
          {uploadedFiles.map((f, i) => {
            const fileConfig = configs[i] || getInitialConfig(f);
            return (
              <div
                key={f.id || i}
                className={`poc-slide ${i === current ? 'active' : ''}`}
                onClick={() => setCurrent(i)}
              >
                {isImage(f.name) && (f.thumbnailLink || f.viewLink || f.file) ? (
                  <ImagePreview
                    file={f}
                    color={fileConfig.color}
                    scale={fileConfig.scale}
                    orientation={fileConfig.orientation}
                  />
                ) : (
                  <div
                    className="poc-slide-doc"
                    style={{
                      filter: fileConfig.color === 'bw' ? 'grayscale(100%)' : undefined,
                      transition: 'filter 0.2s'
                    }}
                  >
                    {isPdfFile(f.name, f.mimeType) && f.viewLink ? (
                      <PDFViewer
                        file={f}
                        pageSelection={fileConfig.pageSelection}
                        selectedPages={fileConfig.pageSelection === 'custom' ? fileConfig.customPages : (fileConfig.pageSelection === 'range' ? parsePageRange(fileConfig.pageRange, f.pageCount || 1) : [])}
                        onPageToggle={handlePageToggle}
                        orientation={fileConfig.orientation}
                        scale={fileConfig.scale}
                      />
                    ) : isDocxFile(f.name, f.mimeType) && f.viewLink ? (
                      <PDFIframePreview 
                        file={f} 
                        pageSelection={fileConfig.pageSelection}
                        selectedPages={fileConfig.pageSelection === 'custom' ? fileConfig.customPages : (fileConfig.pageSelection === 'range' ? parsePageRange(fileConfig.pageRange, f.pageCount || 1) : [])}
                        onPageToggle={handlePageToggle}
                        orientation={fileConfig.orientation}
                        scale={fileConfig.scale}
                        color={fileConfig.color}
                      />
                    ) : (
                        <>
                          <span className="poc-slide-icon">
                            <span>{getFileIcon(f.name)}</span>
                          </span>
                          <span className="poc-slide-ext">{f.name?.split('.').pop().toUpperCase()}</span>
                        </>
                      )
                    }
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Dots */}
        <div className="poc-dots">
          <span className="poc-dots-counter">{current + 1}/{uploadedFiles.length}</span>
          {uploadedFiles.map((_, i) => (
            <button
              key={i}
              className={`poc-dot ${i === current ? 'active' : ''}`}
              onClick={() => setCurrent(i)}
            />
          ))}
        </div>
      </div>

      {/* Config Panel */}
      <div className="poc-config">
        {/* Scale to Fit (in %) */}
        <div className="poc-section">
          <div className="poc-section-labels">
            <span className="poc-section-title">Scale to Fit (in %)</span>
          </div>
          <div className="poc-scale-slider-container" style={{ position: 'relative', padding: '10px 0 10px 0', width: '100%' }}>
            <input
              type="range"
              min="70"
              max="100"
              step="10"
              value={cfg.scale || 90}
              onChange={(e) => updateConfig('scale', parseInt(e.target.value, 10))}
              style={{
                width: '100%',
                accentColor: 'var(--primary, #6c63ff)',
                cursor: 'pointer',
                height: '6px',
                borderRadius: '3px',
                background: 'rgba(255,255,255,0.1)'
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px', fontSize: '0.85rem', color: 'rgba(255,255,255,0.5)', padding: '0 5px' }}>
              {[70, 80, 90, 100].map(val => (
                <span
                  key={val}
                  onClick={() => updateConfig('scale', val)}
                  style={{
                    cursor: 'pointer',
                    fontWeight: (cfg.scale || 90) === val ? '700' : '500',
                    color: (cfg.scale || 90) === val ? 'var(--primary-light, #818cf8)' : 'inherit',
                    transition: 'color 0.2s, font-weight 0.2s'
                  }}
                >
                  {val}%
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Number of copies */}
        <div className="poc-section poc-section-row">
          <div className="poc-section-labels">
            <span className="poc-section-title">Number of copies</span>
            <span className="poc-section-sub">File {current + 1} ({getSelectionSummary(cfg, file?.pageCount)})</span>
          </div>
          <div className="poc-counter">
            <button className="poc-count-btn" onClick={() => updateConfig('copies', Math.max(1, cfg.copies - 1))}>
              <FiMinus size={14} />
            </button>
            <span className="poc-count-val">{cfg.copies}</span>
            <button className="poc-count-btn" onClick={() => updateConfig('copies', cfg.copies + 1)}>
              <FiPlus size={14} />
            </button>
          </div>
        </div>

        {/* Page selection options (only for PDF) */}
        {isPdf ? (
          <div className="poc-section">
            <span className="poc-section-title">Page selection</span>
            <div className="poc-toggle-group-vertical" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {/* Option 1: All Pages */}
              <label
                className={`poc-radio-label ${cfg.pageSelection === 'all' ? 'selected' : ''}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '12px 14px',
                  borderRadius: '12px',
                  border: cfg.pageSelection === 'all' ? '2px solid #16a34a' : '2px solid rgba(255,255,255,0.08)',
                  background: cfg.pageSelection === 'all' ? 'rgba(22, 163, 74, 0.12)' : 'rgba(255,255,255,0.03)',
                  cursor: 'pointer',
                  color: '#fff',
                  fontSize: '0.85rem',
                  fontWeight: 500,
                  transition: 'all 0.15s'
                }}
              >
                <input
                  type="radio"
                  name="pageSelection"
                  value="all"
                  checked={cfg.pageSelection === 'all'}
                  onChange={() => updateConfig('pageSelection', 'all')}
                  style={{ accentColor: '#16a34a' }}
                />
                <span>All {file?.pageCount || 1} pages</span>
              </label>

              {/* Option 2: Page Range (with nested input) */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label
                  className={`poc-radio-label ${cfg.pageSelection === 'range' ? 'selected' : ''}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '12px 14px',
                    borderRadius: '12px',
                    border: cfg.pageSelection === 'range' ? '2px solid #16a34a' : '2px solid rgba(255,255,255,0.08)',
                    background: cfg.pageSelection === 'range' ? 'rgba(22, 163, 74, 0.12)' : 'rgba(255,255,255,0.03)',
                    cursor: 'pointer',
                    color: '#fff',
                    fontSize: '0.85rem',
                    fontWeight: 500,
                    transition: 'all 0.15s'
                  }}
                >
                  <input
                    type="radio"
                    name="pageSelection"
                    value="range"
                    checked={cfg.pageSelection === 'range'}
                    onChange={() => updateConfig('pageSelection', 'range')}
                    style={{ accentColor: '#16a34a' }}
                  />
                  <span>Page range (e.g. 1-2,4,6-10)</span>
                </label>

                {cfg.pageSelection === 'range' && (
                  <div style={{ padding: '2px 4px 6px 4px' }}>
                    <input
                      type="text"
                      placeholder="e.g. 1-2, 4, 6-10"
                      value={cfg.pageRange}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (/^[0-9,-]*$/.test(val)) {
                          updateConfig('pageRange', val);
                        }
                      }}
                      style={{
                        width: '100%',
                        boxSizing: 'border-box',
                        padding: '10px 14px',
                        borderRadius: '8px',
                        border: '1px solid rgba(255,255,255,0.15)',
                        background: '#0d0d1a',
                        color: '#fff',
                        outline: 'none',
                        fontSize: '0.85rem'
                      }}
                    />
                    <span style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.45)', marginTop: '4px', display: 'block' }}>
                      Use commas to separate pages or ranges (e.g. 1-3,5). Selected: {cfg.pages} page(s)
                    </span>
                  </div>
                )}
              </div>

              {/* Option 3: Custom Page Selection (with nested preview instruction / buttons) */}
              {(!isDocx || (file?.pageCount || 1) > 1) && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label
                    className={`poc-radio-label ${cfg.pageSelection === 'custom' ? 'selected' : ''}`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '12px 14px',
                      borderRadius: '12px',
                      border: cfg.pageSelection === 'custom' ? '2px solid #16a34a' : '2px solid rgba(255,255,255,0.08)',
                      background: cfg.pageSelection === 'custom' ? 'rgba(22, 163, 74, 0.12)' : 'rgba(255,255,255,0.03)',
                      cursor: 'pointer',
                      color: '#fff',
                      fontSize: '0.85rem',
                      fontWeight: 500,
                      transition: 'all 0.15s'
                    }}
                  >
                    <input
                      type="radio"
                      name="pageSelection"
                      value="custom"
                      checked={cfg.pageSelection === 'custom'}
                      onChange={() => updateConfig('pageSelection', 'custom')}
                      style={{ accentColor: '#16a34a' }}
                    />
                    <span>Select pages from preview above</span>
                  </label>

                  {cfg.pageSelection === 'custom' && (
                    <div style={{ padding: '2px 4px 6px 4px' }}>
                      <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', display: 'block', textAlign: 'center', marginBottom: '8px' }}>
                        {isDocx 
                          ? "💡 Use the page selector below to select/deselect pages. Selected:" 
                          : "💡 Tap on the page previews above to select/deselect pages. Selected:"}{' '}
                        {formatPageRange(cfg.customPages)} ({cfg.pages} page{cfg.pages !== 1 ? 's' : ''})
                      </span>
                      
                      {isDocx && (
                        <div style={{ 
                          display: 'flex', 
                          flexWrap: 'wrap', 
                          gap: '8px', 
                          justifyContent: 'center',
                          padding: '8px',
                          background: 'rgba(255,255,255,0.03)',
                          borderRadius: '8px',
                          border: '1px solid rgba(255,255,255,0.08)'
                        }}>
                          {Array.from({ length: file?.pageCount || 1 }, (_, index) => index + 1).map((pageNum) => {
                            const isSelected = cfg.customPages.includes(pageNum);
                            return (
                              <button
                                key={pageNum}
                                onClick={() => handlePageToggle(pageNum)}
                                style={{
                                  padding: '6px 12px',
                                  borderRadius: '6px',
                                  border: isSelected ? '1px solid #16a34a' : '1px solid rgba(255,255,255,0.15)',
                                  background: isSelected ? 'rgba(22, 163, 74, 0.2)' : 'transparent',
                                  color: isSelected ? '#16a34a' : 'rgba(255,255,255,0.6)',
                                  fontSize: '0.78rem',
                                  fontWeight: 600,
                                  cursor: 'pointer',
                                  transition: 'all 0.15s'
                                }}
                              >
                                Page {pageNum}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="poc-section poc-section-row">
            <div className="poc-section-labels">
              <span className="poc-section-title">Pages selection</span>
              <span className="poc-section-sub">Non-PDF files print as a single document</span>
            </div>
            <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'rgba(255,255,255,0.6)' }}>All 1 Page</span>
          </div>
        )}

        {/* Paper Size (Fixed to A4 as per rules) */}
        <div className="poc-section poc-section-row">
          <div className="poc-section-labels">
            <span className="poc-section-title">Paper Size</span>
            {/* <span className="poc-section-sub">Standard printing paper size</span> */}
          </div>
          <div
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.15)',
              color: 'rgba(255,255,255,0.6)',
              fontSize: '0.85rem',
              fontWeight: 600
            }}
          >
            A4 (Fixed)
          </div>
        </div>

        {/* Print sides */}
        {(!file || file.pageCount > 1) && (
          <div className="poc-section">
            <span className="poc-section-title">Print sides</span>
            <div className="poc-toggle-group">
              <button
                className={`poc-toggle-btn ${cfg.sides === 'single' ? 'selected' : ''}`}
                onClick={() => updateConfig('sides', 'single')}
                style={{ display: 'flex', justifyContent: 'flex-start' }}
              >
                <FiFileText size={16} />
                <div style={{ marginLeft: '6px' }}>
                  <div>Single Sided</div>
                  <span className="poc-toggle-price" style={{ fontSize: '0.7rem' }}>Print on one side of sheet</span>
                </div>
              </button>
              <button
                className={`poc-toggle-btn ${cfg.sides === 'both' ? 'selected' : ''}`}
                onClick={() => updateConfig('sides', 'both')}
                style={{ display: 'flex', justifyContent: 'flex-start' }}
              >
                <FiFileText size={16} />
                <div style={{ marginLeft: '6px' }}>
                  <div>Double Sided</div>
                  <span className="poc-toggle-price" style={{ fontSize: '0.7rem' }}>Print on both sides of sheet</span>
                </div>
              </button>
            </div>
          </div>
        )}

        {/* Color */}
        <div className="poc-section">
          <span className="poc-section-title">Choose print color</span>
          <div className="poc-toggle-group">
            <button
              className={`poc-toggle-btn ${cfg.color === 'colour' ? 'selected' : ''}`}
              onClick={() => updateConfig('color', 'colour')}
            >
              <span className="poc-color-icon poc-color-icon--colour">⬤</span>
              <div>
                <div>Coloured</div>
                <div className="poc-toggle-price">₹{colorPrice.colour}/page</div>
              </div>
            </button>
            <button
              className={`poc-toggle-btn ${cfg.color === 'bw' ? 'selected' : ''}`}
              onClick={() => updateConfig('color', 'bw')}
            >
              <span className="poc-color-icon poc-color-icon--bw">◑</span>
              <div>
                <div>B &amp; W</div>
                <div className="poc-toggle-price">₹{colorPrice.bw}/page</div>
              </div>
            </button>
          </div>
        </div>

        {/* Orientation */}
        <div className="poc-section">
          <span className="poc-section-title">Choose print orientation</span>
          <div className="poc-toggle-group">
            <button
              className={`poc-toggle-btn ${cfg.orientation === 'portrait' ? 'selected' : ''}`}
              onClick={() => updateConfig('orientation', 'portrait')}
            >
              <span className="poc-orient-icon poc-orient-portrait">▯</span>
              <div>
                <div>Portrait</div>
                <div className="poc-toggle-price">8.3 × 11.7 in</div>
              </div>
            </button>
            <button
              className={`poc-toggle-btn ${cfg.orientation === 'landscape' ? 'selected' : ''}`}
              onClick={() => updateConfig('orientation', 'landscape')}
            >
              <span className="poc-orient-icon poc-orient-landscape">▭</span>
              <div>
                <div>Landscape</div>
                <div className="poc-toggle-price">11.7 × 8.3 in</div>
              </div>
            </button>
          </div>
        </div>

        {/* Apply to all */}
        {uploadedFiles.length > 1 && (
          <div className="poc-apply-all">
            <span>Apply current settings to all files</span>
            <button className="poc-apply-btn" onClick={applyToAll}>Apply</button>
          </div>
        )}
      </div>

      {/* Bottom Bar */}
      <div className="poc-bottom">
        <div className="poc-total-info">
          <span className="poc-total-pages">Total {totalPages} page{totalPages !== 1 ? 's' : ''}</span>
          <span className="poc-total-price">₹{totalPrice}</span>
        </div>
        <button className="poc-cart-btn" onClick={handleAddToCart}>
          {isEditMode ? 'View in Cart →' : 'Add to cart →'}
        </button>
      </div>
      {/* Discard / Update Dialog */}
      {showDiscardDialog && (
        <div className="poc-discard-overlay" onClick={() => setShowDiscardDialog(false)}>
          <div className="poc-discard-dialog" onClick={e => e.stopPropagation()}>
            <p className="poc-discard-title">You have unsaved changes</p>
            <p className="poc-discard-sub">Would you like to update the cart or discard your changes?</p>
            <div className="poc-discard-actions">
              <button className="poc-discard-btn poc-discard-btn--discard" onClick={handleDiscardAndGoBack}>
                Discard
              </button>
              <button className="poc-discard-btn poc-discard-btn--update" onClick={handleUpdateAndGoBack}>
                View in Cart
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrintOrderConfig;
